import { TSeparatorMetadataSchema } from "api/separator.api";
import { DropdownFormProvider } from "components/DropdownFormProvider";
import { ModalContentWrapper } from "components/ModalContentWrapper";
import { TextAreaFormProvider } from "components/TextAreaFormProvider";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import { SwitchFormProvider } from "components/SwitchFormProvider";
import { MonthsCheckboxField } from "components/MonthsCheckboxField";
import { Tips } from "components/Tips";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  getSeparatorLabel,
  separatorTypeToString,
} from "types/separator/separator.helpers";
import {
  Separator,
  SeparatorType,
  isAvskiljare,
} from "types/separator/separator.type";
import { Headline5 } from "components/typography/Headline5";

export const SeparatorMetadataForm = () => {
  const { watch, setValue } = useFormContext<TSeparatorMetadataSchema>();
  const selectedSeparatorType = watch("type");
  const avtalSlamtomning = watch("avtalSlamtomning");
  const avtalVattenprovtagning = watch("avtalVattenprovtagning");

  const isWaterMeasurementPoint =
    selectedSeparatorType === SeparatorType.WaterMeasurementPoint;

  useEffect(() => {
    if (isWaterMeasurementPoint && avtalSlamtomning) {
      setValue("avtalSlamtomning", false);
    }
    if (!isWaterMeasurementPoint && avtalVattenprovtagning) {
      setValue("avtalVattenprovtagning", false);
    }
  }, [
    isWaterMeasurementPoint,
    avtalSlamtomning,
    avtalVattenprovtagning,
    setValue,
  ]);

  const isAvskiljareSeparator = useMemo(() => {
    return isAvskiljare({ type: selectedSeparatorType } as Separator);
  }, [selectedSeparatorType]);

  const SEPARATOR_TYPES = [
    {
      value: SeparatorType.OilAndSlam,
      label: separatorTypeToString(SeparatorType.OilAndSlam),
    },
    {
      value: SeparatorType.Oil,
      label: separatorTypeToString(SeparatorType.Oil),
    },
    {
      value: SeparatorType.Slam,
      label: separatorTypeToString(SeparatorType.Slam),
    },
    {
      value: SeparatorType.WaterMeasurementPoint,
      label: separatorTypeToString(SeparatorType.WaterMeasurementPoint),
    },
    {
      value: SeparatorType.Tvattranna,
      label: separatorTypeToString(SeparatorType.Tvattranna),
    },
    {
      value: SeparatorType.Fritext,
      label: separatorTypeToString(SeparatorType.Fritext),
    },
  ];

  return (
    <ModalContentWrapper className="pb-6">
      <DropdownFormProvider<TSeparatorMetadataSchema>
        items={SEPARATOR_TYPES}
        name="type"
        label={getSeparatorLabel("type")}
        required
        hideClearButton
      />
      {selectedSeparatorType === SeparatorType.Fritext && (
        <TextInputFormProvider<TSeparatorMetadataSchema>
          label="Ange typ"
          name="fritextType"
          autoFocus
          required
        />
      )}
      <TextInputFormProvider<TSeparatorMetadataSchema>
        label={getSeparatorLabel("identifier")}
        name="identifier"
        placeholder="ID eller egen märkning"
        required
      />
      <TextInputFormProvider<TSeparatorMetadataSchema>
        label={getSeparatorLabel("propertyDesignation")}
        name="propertyDesignation"
      />

      <Tips
        id="alksdjljdskfjldskfjsldkfjlsdkfjp90p3i209pofk+2ef"
        paragraphs={[
          `Ange ${getSeparatorLabel(
            "coordinates",
          )} för att enklare hitta objektet vid nästa tillfälle. För rapportering till Avfallsregistret krävs dessutom antingen adress eller koordinater. Öppna Google Maps, högerklicka på platsen och klicka på koordinaterna så kopieras de till urklipp. Klistra sedan in dem nedan. `,
        ]}
      />
      <TextInputFormProvider<TSeparatorMetadataSchema>
        name="coordinates"
        placeholder="xx.xxxxxxxxxxxxxx, yy.yyyyyyyyyyyyyy"
        label={getSeparatorLabel("coordinates")}
        link="https://www.google.se/maps/"
        linkLabel="Öppna Google Maps"
      />
      <TextAreaFormProvider<TSeparatorMetadataSchema>
        name="notes"
        label={getSeparatorLabel("notes")}
      />

      <div className="mt-6">
        <Headline5 className="mb-4 text-sm font-bold text-gray-950">
          Påminnelser{" "}
        </Headline5>
        <div className="space-y-4">
          {/** 5års + 6månaders, endast avskiljare */}
          {isAvskiljareSeparator && (
            <>
              <SwitchFormProvider<TSeparatorMetadataSchema>
                name="avtalFemarskontroll"
                label="Femårsbesiktning"
              />
              <SwitchFormProvider<TSeparatorMetadataSchema>
                name="avtalHalvarsKontroll"
                label="Sexmånaderskontroll"
              />
            </>
          )}

          {/* Slamtömning, alla utom provtagningspunkt */}

          {!isWaterMeasurementPoint && (
            <SwitchFormProvider<TSeparatorMetadataSchema>
              name="avtalSlamtomning"
              label="Tömning"
            />
          )}

          {!isWaterMeasurementPoint && avtalSlamtomning && (
            <MonthsCheckboxField
              name="tomningReminder"
              label="Ange månader som objektet ska tömmas"
              required
            />
          )}

          {/* Vattenprovtagning, endast provtagningspunkt */}
          {isWaterMeasurementPoint && (
            <SwitchFormProvider<TSeparatorMetadataSchema>
              name="avtalVattenprovtagning"
              label="Vattenprovtagning"
            />
          )}

          {isWaterMeasurementPoint && avtalVattenprovtagning && (
            <MonthsCheckboxField
              name="vattenprovtagningMonths"
              label="Ange månader då provtagning ska ske"
              required
            />
          )}
        </div>
      </div>
    </ModalContentWrapper>
  );
};
